import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, Share, X, Smartphone } from "lucide-react";

const DISMISS_KEY = "ecc_install_dismissed_v1";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOS, setShowIOS] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY)) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // small delay so it doesn't fight the hero on load
      setTimeout(() => setVisible(true), 3500);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari fallback: show instructions banner after delay
    if (isIOS()) {
      const t = setTimeout(() => {
        setShowIOS(true);
        setVisible(true);
      }, 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") dismiss();
    } catch { /* noop */ }
    setDeferred(null);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          data-testid="install-prompt"
          className="fixed bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-sm z-40"
        >
          <div className="rounded-2xl bg-forest text-cream border border-forest/50 shadow-hard-lg p-4 md:p-5 flex items-start gap-3 relative">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-butter-deep text-forest shrink-0">
              <Smartphone className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-base leading-tight">Install Brampton C&amp;C on your phone</p>
              {showIOS ? (
                <p className="text-cream/80 text-xs mt-1.5 leading-relaxed">
                  Tap the <Share className="inline h-3.5 w-3.5 mx-0.5 -mt-0.5" /> Share icon in Safari, then
                  <span className="font-semibold text-butter"> Add to Home Screen</span>.
                </p>
              ) : (
                <p className="text-cream/80 text-xs mt-1.5 leading-relaxed">
                  One tap installs the app so you can reorder in seconds — right from your home screen.
                </p>
              )}
              {!showIOS && deferred && (
                <button
                  onClick={install}
                  data-testid="install-app-btn"
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-butter-deep text-forest font-semibold text-xs px-4 py-2 hover:brightness-105 transition"
                >
                  <Download className="h-3.5 w-3.5" /> Install app
                </button>
              )}
            </div>
            <button
              onClick={dismiss}
              data-testid="install-dismiss"
              aria-label="Dismiss install prompt"
              className="shrink-0 grid place-items-center h-7 w-7 rounded-full text-cream/60 hover:bg-white/10 hover:text-cream transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
